import { Spacer } from "@hackthenorth/north";
import { Link, Label } from "north.js";
import React, { useState } from "react";

import { Dropzone, FileLink, Spinner } from "src/shared/components";
import { isValidUrl } from "src/shared/utils/validation";
import { ClosingCeremoniesSlideData } from "src/views/sponsor/perks/sync";
import { PerkStatus, SponsorPerkType } from "src/views/sponsor/perks/types";

import { PerkContainer, PerkError, PerkTextInput } from "../components";
import Header from "../Header";
import { getPerkStatusLabel, perkStatusToBadgeColor } from "../helpers";
import { TextDescription } from "../TextComponents";
import { useFileDropzone } from "../useFileDropzone";
import { PerkData } from "../usePerkState";

export const CLOSING_CEREMONIES_SLIDE_VALIDATION_FIELDS: (keyof ClosingCeremoniesSlideData)[] =
  [];

export const ClosingCeremoniesSlide: React.FC<PerkData> = ({
  getState,
  updateState,
  isReadOnly,
  error,
}) => {
  const {
    dropzoneInputRef,
    fetchedFile,
    error: fileError,
    addFiles,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(
    getState("closing_ceremonies_slide_file") ?? [],
    (files) => updateState("closing_ceremonies_slide_file", files)
  );
  const [spinner, setSpinner] = useState(false);

  return (
    <>
      <Header
        title="Closing Ceremonies Slides"
        subtitle="This perk is for stage time during our closing ceremonies."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              getState("closing_ceremonies_slide_status") as
                | PerkStatus
                | undefined
            )}
            size="sm"
          >
            {getPerkStatusLabel(
              getState("closing_ceremonies_slide_status") as
                | PerkStatus
                | undefined
            )}
          </Label>
        }
      />
      {spinner ? (
        <Spinner text="Uploading file" />
      ) : (
        <PerkContainer
          description={<Description />}
          id={SponsorPerkType.CLOSING_CEREMONIES_SLIDE}
          error={!!error}
          readOnly={isReadOnly}
        >
          {!isReadOnly && (
            <Dropzone
              max={1}
              onFilesUpload={async (e) => {
                setSpinner(true);
                await addFiles(e);
                setSpinner(false);
              }}
              onError={onFileError}
              ref={dropzoneInputRef}
              onBlur={() => setDropzoneErrorMessage("")}
            />
          )}
          {fetchedFile && (
            <>
              {!isReadOnly && <Spacer height={32} />}
              <FileLink file={fetchedFile} deleteFile={deleteFile} />
            </>
          )}
          {fileError && <PerkError error={fileError} />}
          <Spacer height={32} />
          <PerkTextInput
            title={
              <>
                Closing Ceremonies Slide Link{" "}
                <Label color="secondary-light" size="sm">
                  Optional
                </Label>
              </>
            }
            value={getState("closing_ceremonies_slide_link") ?? ""}
            placeholder="Link to your slides here"
            isReadOnly={isReadOnly}
            onChange={(e) =>
              updateState("closing_ceremonies_slide_link", e.target.value)
            }
            hint="Please ensure the link is publicly accessible so we can view it."
            error={
              !isValidUrl(getState("closing_ceremonies_slide_link")) && (
                <>Please enter a valid URL.</>
              )
            }
          />
        </PerkContainer>
      )}
    </>
  );
};

const Description = () => (
  <TextDescription>
    As a gold sponsor, you&apos;ll have the opportunity to present on stage
    during our closing ceremony! The{" "}
    <Link href="https://www.youtube.com/watch?v=PKP8Fws_jsc" target="_blank">
      Hack the North 2025 closing ceremony
    </Link>{" "}
    includes examples of past sponsor presentations. Please note that stage time
    is limited to <strong>1 minute</strong>.
    <br />
    <br />
    You may upload a slide deck as a Google Slides link (preferred) or as a
    .pptx format to be shown during closing ceremonies (optional). Otherwise,
    your company logo will be displayed instead.
  </TextDescription>
);
